import { useQuery } from '@tanstack/react-query';
import { Suspense, useState } from 'react';
import { Link } from 'react-router';

import { api } from '../api.js';
import { useAuth } from '../auth.jsx';
import { useCan } from '../components/guards.jsx';
import {
  ArrowRightIcon,
  Button,
  CheckIcon,
  EmptyState,
  ErrorNote,
  Loading,
  PageHeader,
  SkeletonPanel,
} from '../components/ui.jsx';
import { ProgressBar } from '../components/wizard.jsx';
import { useT } from '../lib/i18n.js';
import { addonOn, adminWidgets, clientWidgets } from '../modules.js';

// Widgets may be lazy components: each gets its own Suspense boundary so one slow chunk does not
// hold back the others.

/**
 * The client dashboard, like the panel's: the client's servers first (a wide widget), then the
 * rest of every module's clientWidgets (unpaid invoices, tickets, credit, announcements) in a grid.
 */
export function ClientDashboard() {
  const { me, addons } = useAuth();
  const t = useT();
  const widgets = clientWidgets().filter((w) => addonOn(w, addons));
  const first = me?.client?.name?.split(' ')[0];
  const wide = widgets.filter((w) => w.wide);
  const rest = widgets.filter((w) => !w.wide);
  return (
    <>
      <PageHeader
        title={first ? t('dashboard.welcome_name', { name: first }) : t('dashboard.welcome')}
        description={t('dashboard.lead')}
      />
      {widgets.length ? (
        <div className="flex flex-col gap-6">
          {wide.map(({ key, Component }) => (
            <Suspense key={key} fallback={<Loading />}>
              <Component />
            </Suspense>
          ))}
          {rest.length ? (
            <div className="grid gap-5 lg:grid-cols-2 2xl:grid-cols-4">
              {rest.map(({ key, Component }) => (
                <div key={key} className="min-w-0 empty:hidden">
                  <Suspense fallback={<Loading />}>
                    <Component />
                  </Suspense>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : (
        <EmptyState title={t('dashboard.empty')} />
      )}
    </>
  );
}

/** The admin dashboard: every module's adminWidgets the staff member may see. */
export function AdminDashboard() {
  const can = useCan();
  const { addons } = useAuth();
  const widgets = adminWidgets().filter((w) => can(w.permission) && addonOn(w, addons));
  return (
    <>
      <PageHeader title="Dashboard" description="Income, what needs a person today, and what is still to set up." />
      <SetupChecklist />
      {widgets.length ? (
        <div className="grid gap-5 xl:grid-cols-2">
          {widgets.map(({ key, Component, wide }) => (
            <div key={key} className={wide ? 'xl:col-span-2' : ''}>
              <Suspense fallback={<SkeletonPanel />}>
                <Component />
              </Suspense>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState title="No widgets yet" />
      )}
    </>
  );
}

/**
 * The first-run checklist: what Octbill still needs before it can take orders, from the real settings
 * (GET /api/admin/system/checklist answers done/not done per item). Each item links to the page
 * that sets it up. It goes away once everything is done, or when hidden on this browser.
 */
const CHECKLIST = {
  stripe: {
    title: 'Connect Stripe',
    body: 'The publishable and secret keys, so clients can pay by card.',
    to: '/admin/billing/gateways',
    action: 'Add the keys',
  },
  mail: {
    title: 'Set up email',
    body: 'Cloudflare SMTP and a From address, for invoices, receipts and password links.',
    to: '/admin/mail/settings',
    action: 'Set up email',
  },
  octail: {
    title: 'Add an Octail panel',
    body: 'The panel new game servers are created on.',
    to: '/admin/servers',
    action: 'Add a panel',
  },
  product: {
    title: 'Create a product',
    body: 'A plan for the store, with its price and the server it gets.',
    to: '/admin/catalog/products',
    action: 'Create a product',
  },
  turnstile: {
    title: 'Turn on Turnstile',
    body: 'Cloudflare Turnstile keys, so bots stay away from sign-up, login and checkout.',
    to: '/admin/settings/security#s-turnstile',
    action: 'Add the keys',
  },
};

const HIDE_KEY = 'octbill.setup-checklist.hidden';

function readHidden() {
  try {
    return window.localStorage.getItem(HIDE_KEY) === '1';
  } catch {
    return false;
  }
}

export function SetupChecklist() {
  const [hidden, setHidden] = useState(readHidden);
  const q = useQuery({ queryKey: ['setup-checklist'], queryFn: () => api('/api/admin/system/checklist') });
  if (hidden) return null;
  if (q.isPending) return <SkeletonPanel rows={4} className="mb-5" />;
  if (q.error) {
    return (
      <div className="mb-5">
        <ErrorNote error={q.error} />
      </div>
    );
  }
  const { items, done, total } = q.data.data;
  if (!total || done === total) return null;
  const hide = () => {
    try {
      window.localStorage.setItem(HIDE_KEY, '1');
    } catch {
      // Private mode: it stays hidden until the page reloads.
    }
    setHidden(true);
  };
  const next = items.find((i) => !i.done)?.key;
  return (
    <section aria-labelledby="setup-title" className="mb-5 rounded-[10px] border border-line bg-surface p-5">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <h2 id="setup-title" className="text-lg leading-tight font-[650]">
            Get ready to take orders
          </h2>
          <p className="mt-0.5 text-sm text-muted">
            {done} of {total} done. Each step links to the page that sets it up.
          </p>
        </div>
        <Button variant="ghost" onClick={hide}>
          Hide the checklist
        </Button>
      </div>
      <ProgressBar value={Math.round((done / total) * 100)} label="Setup progress" className="mt-4" />
      <ol className="mt-4 flex flex-col divide-y divide-line rounded-md border border-line">
        {items.map((item) => {
          const c = CHECKLIST[item.key];
          if (!c) return null;
          return (
            <li
              key={item.key}
              className={`flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 sm:flex-nowrap ${item.key === next ? 'bg-accent/5' : ''}`}
            >
              <span
                aria-hidden="true"
                className={`grid size-5 shrink-0 place-items-center rounded-full border text-xs ${item.done ? 'border-kelp bg-kelp text-white' : item.key === next ? 'border-accent' : 'border-line-strong'}`}
              >
                {item.done ? <CheckIcon /> : null}
              </span>
              <div className="min-w-0 flex-1 basis-48">
                <p className={`font-semibold ${item.done ? 'text-muted' : ''}`}>
                  {c.title}
                  <span className="sr-only">{item.done ? ' (done)' : ' (to do)'}</span>
                </p>
                <p className="text-sm text-muted">{c.body}</p>
              </div>
              {item.done ? (
                <span className="text-sm font-semibold text-kelp sm:ml-auto">Done</span>
              ) : (
                <Link
                  to={c.to}
                  className="inline-flex shrink-0 items-center gap-1 pl-9 text-sm font-semibold text-accent hover:underline sm:ml-auto sm:pl-0"
                >
                  {c.action}
                  <ArrowRightIcon />
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
